import type { CSSProperties } from 'react';
import { Fragment } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
  usePrimaryColor,
} from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFacebookF, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faPaw } from '@fortawesome/free-solid-svg-icons/faPaw';
import { faWheelchair } from '@fortawesome/free-solid-svg-icons/faWheelchair';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

function FairHousingIcon({ size = 40, 'aria-label': ariaLabel = 'Equal Housing Opportunity' }: { size?: number; 'aria-label'?: string }) {
  return (
    <svg
      width={size}
      height={size * 0.96}
      viewBox="0 0 40 38.4"
      fill="currentColor"
      role="img"
      aria-label={ariaLabel}
    >
      <path d="M19.9,0L0,9.9v3h2.9v16H37v-16h3v-3L19.9,0z M33,24.9H7V11.4L19.9,5L33,11.4V24.9z M13,15.7h14v-4.1H13V15.7z M13,21.7h14v-4.1H13V21.7z" />
      <g>
        <path d="M3,31.2h2.2v0.6H3.7v0.4H5v0.6H3.7v0.4h1.5V34H3V31.2z" />
        <path d="M7.8,33.8C7.5,33.9,7.2,34,6.9,34c-0.8,0-1.5-0.6-1.5-1.4v0c0-0.8,0.6-1.4,1.5-1.4s1.5,0.6,1.5,1.4v0c0,0.3-0.1,0.6-0.2,0.8l0.3,0.2l-0.4,0.4L7.8,33.8z M7.2,33.3L6.8,33l0.4-0.4l0.4,0.3c0-0.1,0-0.2,0-0.3v0c0-0.4-0.3-0.7-0.7-0.7c-0.4,0-0.7,0.3-0.7,0.7v0c0,0.4,0.3,0.7,0.7,0.7C7,33.4,7.1,33.3,7.2,33.3z" />
        <path d="M8.8,32.8v-1.5h0.8v1.5c0,0.4,0.2,0.6,0.5,0.6c0.3,0,0.5-0.2,0.5-0.6v-1.5h0.8v1.5c0,0.9-0.5,1.3-1.3,1.3C9.2,34,8.8,33.6,8.8,32.8z" />
        <path d="M12.6,31.2h0.7l1.2,2.8h-0.8l-0.2-0.5h-1.1L12.2,34h-0.8L12.6,31.2z M13.3,32.9L13,32.1l-0.3,0.8H13.3z" />
        <path d="M14.8,31.2h0.8v2.1h1.3V34h-2.1V31.2z" />
        <path d="M18.3,31.2H19v1h1v-1h0.8V34H20v-1h-1v1h-0.8V31.2z" />
        <path d="M21.1,32.6L21.1,32.6c0-0.8,0.6-1.4,1.5-1.4s1.5,0.6,1.5,1.4v0c0,0.8-0.6,1.4-1.5,1.4C21.8,34,21.1,33.4,21.1,32.6zM23.3,32.6L23.3,32.6c0-0.4-0.3-0.7-0.7-0.7c-0.4,0-0.7,0.3-0.7,0.7v0c0,0.4,0.3,0.7,0.7,0.7C23,33.4,23.3,33,23.3,32.6z" />
        <path d="M24.4,32.8v-1.5h0.8v1.5c0,0.4,0.2,0.6,0.5,0.6c0.3,0,0.5-0.2,0.5-0.6v-1.5H27v1.5c0,0.9-0.5,1.3-1.3,1.3C24.9,34,24.4,33.6,24.4,32.8z" />
        <path d="M27.2,33.6l0.4-0.5c0.3,0.2,0.6,0.3,0.9,0.3c0.2,0,0.3-0.1,0.3-0.2v0c0-0.1-0.1-0.2-0.5-0.3c-0.6-0.1-1-0.3-1-0.9v0c0-0.5,0.4-0.9,1.1-0.9c0.5,0,0.8,0.1,1.1,0.4l-0.4,0.5c-0.3-0.2-0.5-0.3-0.8-0.3c-0.2,0-0.3,0.1-0.3,0.2v0c0,0.1,0.1,0.2,0.5,0.3c0.6,0.1,1,0.3,1,0.9v0c0,0.6-0.4,0.9-1.1,0.9C28,34,27.6,33.9,27.2,33.6z" />
        <path d="M30,31.2h0.8V34H30V31.2z" />
        <path d="M31.3,31.2H32l1.1,1.4v-1.4h0.8V34h-0.7L32,32.5V34h-0.8V31.2z" />
      </g>
    </svg>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

/**
 * Returns true when a color is "dark" (i.e. white text reads well on top of it).
 * Accepts #rgb / #rrggbb hex strings. Unknown/unparseable values are treated as
 * dark, since the footer background defaults to a dark brand color.
 */
function isDarkColor(color: string | null | undefined): boolean {
  if (!color) return true;
  const match = color.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return true;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Removes a trailing country segment (e.g. "USA", "United States") from an
 * address string so the footer shows just the street / city / state / zip.
 */
function stripCountry(address: string): string {
  return address
    .replace(/,?\s*(u\.?\s*s\.?\s*a\.?|united states(?:\s+of\s+america)?|u\.?\s*s\.?)\s*$/i, '')
    .replace(/,\s*$/, '')
    .trim();
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

/**
 * NJ 2-1-1 emergency resources only apply to New Jersey properties. Accepts
 * common state values ("NJ", "N.J.", "New Jersey") and returns false when the
 * state is missing (company-theme / no-property preview).
 */
function isNewJerseyState(state: string | null | undefined): boolean {
  if (!state) return false;
  const normalized = state.trim().toLowerCase().replace(/\./g, '');
  return normalized === 'nj' || normalized === 'new jersey';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const DAY_FULL: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface DayHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function hoursDisplay(entry: OfficeHour | undefined): string {
  if (!entry || !hasValue(entry.open_time) || !hasValue(entry.close_time)) {
    return 'Closed';
  }
  return `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;
}

/**
 * Collapses consecutive days that share the same open window into a single
 * range row (e.g. Mon–Fri, Sat–Sun) so footer / contact hours stay compact.
 */
export function listOfficeHours(raw: OfficeHour[]): DayHours[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const entries = DAY_ORDER.map((key) => ({
    key,
    hours: hoursDisplay(byDay.get(key)),
  }));

  const rows: DayHours[] = [];
  let i = 0;
  while (i < entries.length) {
    let j = i;
    while (j + 1 < entries.length && entries[j + 1].hours === entries[i].hours) {
      j += 1;
    }
    const label =
      j > i
        ? `${DAY_FULL[entries[i].key]} - ${DAY_FULL[entries[j].key]}`
        : DAY_FULL[entries[i].key];
    const shortLabel =
      j > i
        ? `${DAY_SHORT[entries[i].key]} - ${DAY_SHORT[entries[j].key]}`
        : DAY_SHORT[entries[i].key];
    rows.push({ label, shortLabel, hours: entries[i].hours });
    i = j + 1;
  }
  return rows;
}

// ============================================================================
// Slot Group Definitions (original AION footer properties)
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const emergencyHelpLinkDef: SlotGroupDefinition = {
  label: 'Emergency Help Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Need Help? Start Here!' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const emergencyBadgeDef: SlotGroupDefinition = {
  label: 'Emergency Resource Badge',
  fields: {
    image: {
      label: 'Badge Image',
      type: 'image',
      defaultValue: 'https://lirp.cdn-website.com/5e7be255/dms3rep/multi/opt/NJ211-Logo-web-352px-79w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'NJ 2-1-1 — Get connected. Get answers.' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const REPLI_LOGO_WHITE =
  'https://irp.cdn-website.com/ae60dd6a/dms3rep/multi/Repli_White+logo+gradient+punctuation.svg';
const REPLI_LOGO_GRADIENT =
  'https://irp.cdn-website.com/ae60dd6a/dms3rep/multi/Repli_Gradient_Logo1.svg';

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const COLUMN_LABEL_CLASS =
  'font-heading text-xl md:text-2xl font-normal mb-4';

// ============================================================================
// Social icon link — a plain icon (no circular container) for every platform.
// ============================================================================

interface SocialIconLinkProps {
  url: string | null | undefined;
  target: string | null | undefined;
  label: string;
  icon: IconDefinition;
}

function SocialIconLink({ url, target, label, icon }: SocialIconLinkProps) {
  return url ? (
    <a
      href={url}
      target={target || '_blank'}
      rel="noopener noreferrer"
      aria-label={label}
      className="footer-social-link"
    >
      <FontAwesomeIcon icon={icon} className="text-2xl" />
    </a>
  ) : (
    <span className="footer-social-link footer-social-link--disabled" aria-hidden="true">
      <FontAwesomeIcon icon={icon} className="text-2xl" />
    </span>
  );
}

// ============================================================================
// Component — original AION properties, Aster-inspired layout
// ============================================================================

export function Footer() {
  useComfyCtaStyles();

  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressState = usePropertyField('property_address_state') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();
  const showEmergencySection = isNewJerseyState(propertyAddressState);

  const companyColorLogo = usePropertyField('property_company_logo') as string | null | undefined;
  const companyWhiteLogo = usePropertyField('property_company_mono_white_logo') as string | null | undefined;
  const companyBlackLogo = usePropertyField('property_company_mono_black_logo') as string | null | undefined;

  const propertyPrimaryLogo = usePropertyField('property_primary_logo') as string | null | undefined;
  const propertySecondaryLogo = usePropertyField('property_secondary_logo') as string | null | undefined;
  const propertyWhiteLogo = usePropertyField('property_mono_white_logo') as string | null | undefined;
  const propertyBlackLogo = usePropertyField('property_mono_black_logo') as string | null | undefined;

  const footerBgColor = usePrimaryColor();
  const footerBgIsDark = isDarkColor(footerBgColor);
  const autoManagedByLogo = footerBgIsDark
    ? (companyWhiteLogo || companyColorLogo || '')
    : (companyColorLogo || companyBlackLogo || '');
  const autoCommunityLogo = footerBgIsDark
    ? (propertyWhiteLogo || propertyPrimaryLogo || propertySecondaryLogo || '')
    : (propertyPrimaryLogo || propertyBlackLogo || propertySecondaryLogo || '');

  const footerToneStyle: CSSProperties = footerBgIsDark
    ? ({
        '--footer-fg': '#ffffff',
        '--footer-fg-muted': 'rgba(255, 255, 255, 0.9)',
        '--footer-fg-subtle': 'rgba(255, 255, 255, 0.8)',
        '--footer-fg-faint': 'rgba(255, 255, 255, 0.5)',
        '--footer-border': 'rgba(255, 255, 255, 0.2)',
      } as CSSProperties)
    : ({
        '--footer-fg': '#111111',
        '--footer-fg-muted': 'rgba(17, 17, 17, 0.9)',
        '--footer-fg-subtle': 'rgba(17, 17, 17, 0.75)',
        '--footer-fg-faint': 'rgba(17, 17, 17, 0.5)',
        '--footer-border': 'rgba(17, 17, 17, 0.15)',
      } as CSSProperties);

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const emergencyHelpLink = useSlotGroup('footer.emergency_help_link', emergencyHelpLinkDef);
  const emergencyBadge = useSlotGroup('footer.emergency_badge', emergencyBadgeDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const tiktokLink = useSlotGroup('footer.tiktok_link', tiktokLinkDef);
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const dayHours = officeHours && officeHours.length > 0
    ? listOfficeHours(officeHours)
    : [];

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '555-555-5555';

  const addressText = stripCountry(addressLink.text || '123 Main Street, City, ST 00000');
  // Force the standard two-line mailing layout (street / city-state-zip) instead
  // of letting a narrow max-width wrap a single string onto three lines.
  const addressCommaIdx = addressText.indexOf(',');
  const addressLine1 =
    addressCommaIdx !== -1 ? addressText.slice(0, addressCommaIdx).trim() : addressText;
  const addressLine2 =
    addressCommaIdx !== -1 ? addressText.slice(addressCommaIdx + 1).trim() : '';

  return (
    <footer
      className="footer-root"
      style={footerToneStyle}
      data-footer-tone={footerBgIsDark ? 'dark' : 'light'}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ---- Community Logo (Aster: centered wordmark) ---- */}
        <div className="pt-12 pb-8 md:pt-16 text-center">
          <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
            {(() => {
              const communityLogoSrc = logo.image || autoCommunityLogo;
              return (
                <a
                  href={logo.url || '/'}
                  target={logo.url ? logo._target || '_blank' : undefined}
                  rel={logo.url ? 'noopener noreferrer' : undefined}
                  className="inline-block"
                >
                  {communityLogoSrc ? (
                    <VibesitesImage
                      url={communityLogoSrc}
                      alt={logo.alt || 'Community Logo'}
                      sizes="(min-width: 768px) 320px, 240px"
                      className="h-16 md:h-24 w-auto max-w-[320px] object-contain mx-auto"
                    />
                  ) : (
                    <span className="footer-fallback-logo font-heading text-2xl">
                      Our Community
                    </span>
                  )}
                </a>
              );
            })()}
          </EditableSlotGroup>
        </div>

        {/* ---- Info Columns (Aster: Contact / Visit Us / Office Hours / Follow Us) ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 pt-4 pb-10 text-center">

          <div className="flex flex-col items-center">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>Contact us</p>"
              as="h3"
              className={COLUMN_LABEL_CLASS}
            />
            <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                className="footer-link block font-body text-base md:text-lg dniphone dniphonehref"
              >
                {phoneDisplay}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col items-center">
            <RichTextContent
              path="footer.visit_us_label"
              defaultValue="<p>Visit Us</p>"
              as="h3"
              className={COLUMN_LABEL_CLASS}
            />
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-link block font-body text-base md:text-lg mx-auto text-center leading-snug"
              >
                <span className="block">{addressLine1}</span>
                {addressLine2 ? <span className="block">{addressLine2}</span> : null}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col items-center">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>Office Hours</p>"
              as="h3"
              className={COLUMN_LABEL_CLASS}
            />
            {dayHours.length > 0 ? (
              <div className="inline-grid grid-cols-[auto_auto] gap-x-8 gap-y-2 font-body text-sm md:text-base footer-hours-row text-left">
                {dayHours.map((entry, index) => (
                  <Fragment key={index}>
                    <span>{entry.shortLabel}</span>
                    <span>{entry.hours}</span>
                  </Fragment>
                ))}
              </div>
            ) : (
              <p className="footer-hours-empty font-body text-sm">Office hours will be displayed here.</p>
            )}
          </div>

          <div className="flex flex-col items-center">
            <RichTextContent
              path="footer.social_label"
              defaultValue="<p>Follow Us</p>"
              as="h3"
              className={COLUMN_LABEL_CLASS}
            />
            <div className="flex items-center justify-center gap-3">
              <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                <SocialIconLink
                  url={facebookLink.url}
                  target={facebookLink._target}
                  label={facebookLink.label || 'Facebook'}
                  icon={faFacebookF}
                />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                <SocialIconLink
                  url={instagramLink.url}
                  target={instagramLink._target}
                  label={instagramLink.label || 'Instagram'}
                  icon={faInstagram}
                />
              </EditableSlotGroup>
              {tiktokLink.url ? (
                <EditableSlotGroup groupPath="footer.tiktok_link" definition={tiktokLinkDef}>
                  <SocialIconLink
                    url={tiktokLink.url}
                    target={tiktokLink._target}
                    label={tiktokLink.label || 'TikTok'}
                    icon={faTiktok}
                  />
                </EditableSlotGroup>
              ) : null}
            </div>
          </div>

        </div>

        {/* ---- Emergency Contact — NJ-only (original AION) ---- */}
        {showEmergencySection ? (
          <div className="footer-emergency pb-10 text-center">
            <RichTextContent
              path="footer.emergency_heading"
              defaultValue="<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>"
              as="p"
              className="footer-emergency-heading font-body text-sm md:text-base leading-snug"
            />
            <RichTextContent
              path="footer.emergency_contact_line"
              defaultValue={'<p><span data-type="field-token" data-field="property_name"></span> Service Manager: <span data-type="field-token" data-field="property_call_tracking_phone"></span></p>'}
              as="p"
              className="footer-emergency-contact font-body text-sm md:text-base leading-snug mt-1"
            />
            <EditableSlotGroup groupPath="footer.emergency_help_link" definition={emergencyHelpLinkDef}>
              <a
                href={emergencyHelpLink.url || 'https://nj211.org/'}
                target={emergencyHelpLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-emergency-help font-body text-sm md:text-base font-bold inline-block mt-2"
              >
                {emergencyHelpLink.text || 'Need Help? Start Here!'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.emergency_badge" definition={emergencyBadgeDef} className="mt-4 inline-block">
              {(() => {
                const badgeSrc = emergencyBadge.image
                  || 'https://lirp.cdn-website.com/5e7be255/dms3rep/multi/opt/NJ211-Logo-web-352px-79w.png';
                const badgeAlt = emergencyBadge.alt || 'NJ 2-1-1 — Get connected. Get answers.';
                const badgeImg = (
                  <VibesitesImage
                    url={badgeSrc}
                    alt={badgeAlt}
                    sizes="176px"
                    className="footer-emergency-badge h-auto w-auto max-h-14 max-w-[176px] object-contain"
                  />
                );
                return emergencyBadge.url ? (
                  <a
                    href={emergencyBadge.url}
                    target={emergencyBadge._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={badgeAlt}
                    className="inline-block opacity-90 hover:opacity-100 transition-opacity"
                  >
                    {badgeImg}
                  </a>
                ) : (
                  badgeImg
                );
              })()}
            </EditableSlotGroup>
          </div>
        ) : null}

        {/* ---- Compliance Icons ---- */}
        <div className="footer-compliance flex items-center justify-center gap-8 md:gap-10 pb-5">
          <FairHousingIcon size={32} aria-label="Equal Housing Opportunity" />
          <FontAwesomeIcon icon={faPaw} style={{ width: 24, height: 24 }} aria-label="Pet Friendly" role="img" />
          <FontAwesomeIcon icon={faWheelchair} style={{ width: 24, height: 24 }} aria-label="Wheelchair Accessible" role="img" />
        </div>

        {/* ---- Legal Nav + Copyright ---- */}
        <div className="flex flex-col items-center gap-2 pb-8 text-center font-body text-xs">
          <nav
            className="footer-legal flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-body text-xs"
            aria-label="Legal links"
          >
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a
                href={accessibilityLink.url || '/accessibility'}
                target={accessibilityLink._target || '_self'}
                className="footer-legal-link"
              >
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span className="footer-legal-sep" aria-hidden="true">•</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a
                href={sitemapLink.url || '/sitemap'}
                target={sitemapLink._target || '_self'}
                className="footer-legal-link"
              >
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
            <span className="footer-legal-sep" aria-hidden="true">•</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a
                href={privacyPolicyLink.url || '/privacy-policy'}
                target={privacyPolicyLink._target || '_self'}
                className="footer-legal-link"
              >
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
          </nav>

          <RichTextContent
            path="footer.copyright"
            defaultValue={'<p>&copy; 2026 All Rights Reserved. <span data-type="field-token" data-field="property_name"></span></p>'}
            as="p"
            className="footer-copyright font-body text-xs m-0"
          />
        </div>

        {/* ---- Divider ---- */}
        <div className="footer-divider border-t" />

        {/* ---- Bottom Bar: Managed By / Design By ---- */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-y-6 gap-x-6 py-6 font-body text-xs">

          <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
            <a
              href={managedByLogo.url || '#'}
              target={managedByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center justify-center md:justify-start gap-3 footer-caption font-body text-xs hover:opacity-100 transition-opacity opacity-90"
            >
              <RichTextContent
                path="footer.managed_by_label"
                defaultValue="<p>Professionally Managed By</p>"
                as="span"
                className="footer-caption font-body text-xs"
              />
              {(() => {
                const managedLogoSrc = managedByLogo.image || autoManagedByLogo;
                return managedLogoSrc ? (
                  <VibesitesImage
                    url={managedLogoSrc}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    sizes="180px"
                    className="h-11 w-auto max-w-[180px] object-contain"
                  />
                ) : null;
              })()}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
            {(() => {
              const designLogoSrc =
                designByLogo.image
                || (footerBgIsDark ? REPLI_LOGO_WHITE : REPLI_LOGO_GRADIENT);
              const designLogoAlt = designByLogo.alt || 'Repli';
              return (
                <a
                  href={designByLogo.url || 'https://www.repli360.com/'}
                  target={designByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label={designLogoAlt}
                  className="flex items-center justify-center md:justify-end gap-3 footer-caption font-body text-xs hover:opacity-100 transition-opacity opacity-90"
                >
                  <RichTextContent
                    path="footer.design_by_label"
                    defaultValue="<p>Website Designed By</p>"
                    as="span"
                    className="footer-caption font-body text-xs"
                  />
                  <VibesitesImage
                    url={designLogoSrc}
                    alt={designLogoAlt}
                    sizes="144px"
                    className="h-8 w-auto max-w-[144px] object-contain"
                  />
                </a>
              );
            })()}
          </EditableSlotGroup>

        </div>
      </div>

      <style>{`
        .footer-root {
          position: relative;
          isolation: isolate;
          background-color: var(--vibe-color-primary, #1a1a1a);
          color: var(--footer-fg, #fff);
        }
        .footer-fallback-logo {
          color: var(--footer-fg, #fff);
        }
        .footer-link {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
          transition: color 0.25s ease;
        }
        .footer-link:hover {
          color: var(--footer-fg, #fff);
        }
        .footer-hours-row {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-hours-empty {
          color: var(--footer-fg-faint, rgba(255, 255, 255, 0.5));
        }
        .footer-divider {
          border-color: var(--footer-border, rgba(255, 255, 255, 0.2));
        }
        .footer-caption {
          color: var(--footer-fg-subtle, rgba(255, 255, 255, 0.8));
        }
        .footer-legal {
          color: var(--footer-fg-subtle, rgba(255, 255, 255, 0.8));
        }
        .footer-legal-link {
          color: inherit;
          transition: color 0.25s ease;
        }
        .footer-legal-link:hover {
          color: var(--footer-fg, #fff);
        }
        .footer-legal-sep {
          color: var(--footer-fg-subtle, rgba(255, 255, 255, 0.8));
        }
        .footer-compliance {
          color: var(--footer-fg, #fff);
        }
        .footer-copyright {
          color: var(--footer-fg-subtle, rgba(255, 255, 255, 0.8));
        }
        .footer-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--footer-fg, #fff);
          transition: color 0.25s ease, opacity 0.25s ease;
        }
        a.footer-social-link:hover {
          color: var(--footer-fg, #fff);
          opacity: 0.8;
        }
        .footer-social-link--disabled {
          opacity: 1;
        }
        .footer-emergency {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-emergency-heading {
          color: var(--footer-fg, #fff);
          font-weight: 400;
        }
        .footer-emergency-contact {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-emergency-help {
          color: var(--footer-fg, #fff);
          text-decoration: underline;
          transition: opacity 0.25s ease;
        }
        .footer-emergency-help:hover {
          opacity: 0.8;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact us</p>',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>Visit Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.social_label': {
      label: 'Social Media Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us</p>',
    },
    'footer.emergency_heading': {
      label: 'Emergency Contact Heading',
      type: 'richtext',
      defaultValue: '<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>',
    },
    'footer.emergency_contact_line': {
      label: 'Emergency Contact Line',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_name"></span> Service Manager: <span data-type="field-token" data-field="property_call_tracking_phone"></span></p>',
    },
    'footer.managed_by_label': {
      label: 'Managed By Label',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed By</p>',
    },
    'footer.design_by_label': {
      label: 'Designed By Label',
      type: 'richtext',
      defaultValue: '<p>Website Designed By</p>',
    },
    'footer.copyright': {
      label: 'Copyright Line',
      type: 'richtext',
      defaultValue: '<p>&copy; 2026 All Rights Reserved. <span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.emergency_help_link': emergencyHelpLinkDef,
    'footer.emergency_badge': emergencyBadgeDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.tiktok_link': tiktokLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
